import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import { updateBrandTeamMemberEndpoint } from '../../../api/endpoints';
import { BrandTeamMemberData } from './data/type';
import { RmnCategoryNode } from '../../../types/rmn';

// Inline per-user RMN allowlists on the internal Brand > Team page
// (rmn:true networks, 'brand team member' / 'brand sales' rows only).
// Each picker saves on change via the member PATCH with only its own id
// array (replace-set; empty = full access). Server-side enforcement lives
// in the API's lib/rmnPermissions — these are the editing surface.

export interface PickerOption {
  id: number;
  label: string;
  depth?: number;
  deactivated?: boolean;
}

// Depth-first flatten of the network's category tree with breadcrumb
// labels ("A › B › C") so a deep pick reads unambiguously in a chip.
// Soft-deleted (removed) nodes are skipped — the API rejects them.
export const flattenRmnTreePaths = (
  nodes: Array<RmnCategoryNode & { removed?: boolean }>,
  prefix: string[] = [],
): PickerOption[] =>
  (nodes || []).flatMap((n) => {
    const path = [...prefix, n.name];
    const self = n.removed ? [] : [{ id: n.id, label: path.join(' › '), depth: prefix.length }];
    return [...self, ...flattenRmnTreePaths(n.children || [], path)];
  });

interface RmnPermissionPickersProps {
  brandId: number;
  member: BrandTeamMemberData;
  brandOptions: PickerOption[];
  categoryOptions: PickerOption[];
  // Server-confirmed permissions after a save — the page keeps a local
  // copy of the team so chips persist without a full refetch.
  onSaved: (userid: number, patch: Partial<BrandTeamMemberData>) => void;
}

type Axis = 'brand' | 'category';

const AXIS_FIELD: Record<Axis, 'brand_permission_ids' | 'category_permission_ids'> = {
  brand: 'brand_permission_ids',
  category: 'category_permission_ids',
};

export const RmnPermissionPickers: React.FC<RmnPermissionPickersProps> = ({
  brandId,
  member,
  brandOptions,
  categoryOptions,
  onSaved,
}) => {
  const [saving, setSaving] = useState<Axis | null>(null);

  // Current picks, resolved against the option lists so labels stay
  // consistent; a saved id that is no longer offered (supplier unlinked,
  // category removed) still renders — greyed — so it can be cleared.
  const resolve = (
    saved: Array<{ id: number; name: string; deactivated?: boolean }> | undefined,
    options: PickerOption[],
  ): PickerOption[] =>
    (saved || []).map((s) => {
      const opt = options.find((o) => o.id === s.id);
      return opt
        ? { ...opt, deactivated: s.deactivated === true }
        : { id: s.id, label: s.name, deactivated: true };
    });

  const [brandValue, setBrandValue] = useState<PickerOption[]>(() =>
    resolve(member.brand_permissions, brandOptions),
  );
  const [categoryValue, setCategoryValue] = useState<PickerOption[]>(() =>
    resolve(member.category_permissions, categoryOptions),
  );
  useEffect(() => {
    setBrandValue(resolve(member.brand_permissions, brandOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.brand_permissions, brandOptions]);
  useEffect(() => {
    setCategoryValue(resolve(member.category_permissions, categoryOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.category_permissions, categoryOptions]);

  const save = async (axis: Axis, next: PickerOption[], previous: PickerOption[]) => {
    const setValue = axis === 'brand' ? setBrandValue : setCategoryValue;
    setValue(next);
    setSaving(axis);
    try {
      const { data } = await axiosInstant.patch(
        updateBrandTeamMemberEndpoint(brandId, member.userid),
        { [AXIS_FIELD[axis]]: next.map((o) => o.id) },
      );
      const user = (data && data.user) || {};
      onSaved(member.userid, {
        brand_permissions: user.brand_permissions || [],
        category_permissions: user.category_permissions || [],
      });
      toast.success(
        next.length === 0
          ? `${axis === 'brand' ? 'Brand' : 'Category'} permissions cleared — full access`
          : 'Permissions saved',
      );
    } catch (error: any) {
      const code = error && error.response && error.response.data && error.response.data.error;
      toast.error(code ? `Couldn't save permissions (${code})` : "Couldn't save permissions");
      setValue(previous);
    } finally {
      setSaving(null);
    }
  };

  const renderPicker = (
    axis: Axis,
    label: string,
    options: PickerOption[],
    value: PickerOption[],
  ) => (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={options}
      value={value}
      disabled={saving !== null}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      onChange={(_e, next) => save(axis, next, value)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span style={{ paddingLeft: (option.depth || 0) * 12 }}>{option.label}</span>
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            size="small"
            label={option.label}
            title={option.deactivated ? 'No longer in the network — grants nothing' : undefined}
            sx={option.deactivated ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? 'Full access' : ''}
          helperText={saving === axis ? 'Saving…' : undefined}
        />
      )}
      sx={{ minWidth: 260, flex: 1 }}
    />
  );

  return (
    <Box className="flex flex-col md:flex-row gap-3 mt-3 w-full">
      {renderPicker('brand', 'Brand permissions', brandOptions, brandValue)}
      {renderPicker('category', 'Category permissions', categoryOptions, categoryValue)}
      {brandOptions.length === 0 && categoryOptions.length === 0 && (
        <Typography variant="caption" color="textSecondary">
          No network brands or categories to pick from yet.
        </Typography>
      )}
    </Box>
  );
};
